import { useCallback, useEffect, useState } from 'react'

import {
  getPractitionerMembership,
  openMembershipBillingPortal,
  signOutPractitioner,
} from '../utils/membershipApi'

import {
  PractitionerApiError,
  getStoredPractitionerSession,
  signInPractitioner,
} from '../utils/practitionerApi'

import type {
  PractitionerSession,
} from '../types/practitionerSignup'

import type {
  PractitionerMembership,
} from '../types/membership'

export interface MembershipPreview {
  membershipPreview?: {
    membership?: PractitionerMembership
    state?: 'ready' | 'none' | 'signed_out' | 'unavailable'
  }
}

type MembershipLoadState =
  | {
      kind: 'loading'
    }
  | {
      kind: 'signed_out'
    }
  | {
      kind: 'none'
    }
  | {
      kind: 'ready'
      membership: PractitionerMembership
    }
  | {
      kind: 'unavailable'
      notConnected: boolean
    }

export function useMembershipManagement(
  preview?: MembershipPreview,
) {
  const [load, setLoad] = useState<MembershipLoadState>({
    kind: 'loading',
  })

  const [session, setSession] =
    useState<PractitionerSession | null>(null)

  const [signingIn, setSigningIn] = useState(false)
  const [signInError, setSignInError] =
    useState<string | null>(null)

  const [actionBusy, setActionBusy] =
    useState<'manage' | null>(null)

  const [actionError, setActionError] =
    useState<string | null>(null)

  /**
   * Load membership for the current practitioner.
   */
  const loadMembership = useCallback(
    async (currentSession?: PractitionerSession | null) => {
      // Design preview support only.
      if (preview?.membershipPreview) {
        const previewState = preview.membershipPreview

        if (
          previewState.state === 'ready' &&
          previewState.membership
        ) {
          setLoad({
            kind: 'ready',
            membership: previewState.membership,
          })
          return
        }

        if (previewState.state === 'none') {
          setLoad({ kind: 'none' })
          return
        }

        if (previewState.state === 'signed_out') {
          setLoad({ kind: 'signed_out' })
          return
        }

        if (previewState.state === 'unavailable') {
          setLoad({
            kind: 'unavailable',
            notConnected: false,
          })
          return
        }
      }

      setLoad({ kind: 'loading' })
      setActionError(null)

      const activeSession =
        currentSession ?? getStoredPractitionerSession()

      if (!activeSession) {
        setSession(null)
        setLoad({ kind: 'signed_out' })
        return
      }

      setSession(activeSession)

      try {
        const membership =
          await getPractitionerMembership(activeSession)

        if (!membership) {
          setLoad({ kind: 'none' })
          return
        }

        setLoad({
          kind: 'ready',
          membership,
        })
      } catch (error) {
        if (
          error instanceof PractitionerApiError &&
          error.code === 'invalid_credentials'
        ) {
          setSession(null)
          setLoad({ kind: 'signed_out' })
          return
        }

        if (
          error instanceof PractitionerApiError &&
          error.code === 'not_configured'
        ) {
          setLoad({
            kind: 'unavailable',
            notConnected: true,
          })
          return
        }

        console.error(
          'Unable to load practitioner membership:',
          error,
        )

        setLoad({
          kind: 'unavailable',
          notConnected: false,
        })
      }
    },
    [preview],
  )

  /**
   * Initial membership load.
   */
  useEffect(() => {
    void loadMembership()
  }, [loadMembership])

  /**
   * Sign practitioner in and immediately load membership.
   */
  const signIn = useCallback(
    async (email: string, password: string) => {
      setSigningIn(true)
      setSignInError(null)

      try {
        const newSession = await signInPractitioner(
          email,
          password,
        )

        setSession(newSession)

        await loadMembership(newSession)
      } catch (error) {
        console.error(
          'Practitioner membership sign-in failed:',
          error,
        )

        if (error instanceof PractitionerApiError) {
          setSignInError(error.message)
        } else {
          setSignInError(
            'We couldn’t sign you in. Please check your details and try again.',
          )
        }
      } finally {
        setSigningIn(false)
      }
    },
    [loadMembership],
  )

  /**
   * Open Stripe Customer Portal.
   *
   * Stripe handles:
   * - cancellation
   * - payment methods
   * - billing information
   * - subscription management
   */
  const manage = useCallback(async () => {
    if (!session) {
      setLoad({ kind: 'signed_out' })
      return
    }

    setActionBusy('manage')
    setActionError(null)

    try {
      await openMembershipBillingPortal(session)
    } catch (error) {
      console.error(
        'Unable to open Stripe Customer Portal:',
        error,
      )

      if (
        error instanceof PractitionerApiError &&
        error.code === 'invalid_credentials'
      ) {
        setSession(null)
        setLoad({ kind: 'signed_out' })
        return
      }

      setActionError(
        error instanceof Error
          ? error.message
          : 'We couldn’t open membership settings right now.',
      )
    } finally {
      setActionBusy(null)
    }
  }, [session])

  /**
   * Sign out: revoke the Supabase session (best effort) and clear it locally.
   */
  const [signingOut, setSigningOut] = useState(false)
  const [signedOut, setSignedOut] = useState(false)

  const signOut = useCallback(async () => {
    setSigningOut(true)
    try {
      await signOutPractitioner(session)
    } finally {
      setSession(null)
      setActionError(null)
      setSignInError(null)
      setLoad({ kind: 'signed_out' })
      setSignedOut(true)
      setSigningOut(false)
    }
  }, [session])

  /**
   * Reload membership from Supabase.
   */
  const reload = useCallback(async () => {
    await loadMembership(session)
  }, [loadMembership, session])

  return {
    load,
    session,

    signOut,
    signingOut,
    signedOut,

    signingIn,
    signInError,
    signIn,

    actionBusy,
    actionError,

    manage,
    reload,
  }
}